import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { FireExtinguisher, Plus, Trash2, Upload, Download, Eye, FileText, Loader2, CheckCircle2, AlertTriangle, Sparkles, Pencil, UserX, Gauge, Users, ShieldCheck, XOctagon, FolderUp, XCircle } from "lucide-react";
import { api, useAuth, formatApiError, downloadFile, canDo } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import EmployeePicker from "../components/EmployeePicker";
import ExportDialog from "../components/ExportDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const STATUTS = {
  valide: { label: "Valide", classes: "bg-emerald-950 text-emerald-400 border-emerald-700" },
  a_recycler: { label: "À revoir", classes: "bg-amber-950 text-amber-400 border-amber-700" },
  depasse: { label: "Expirée", classes: "bg-red-950 text-red-400 border-red-700" },
  inconnu: { label: "Inconnu", classes: "bg-gray-800 text-gray-300 border-gray-600" },
};

const FILTRES = [
  { key: "tous", label: "Tous" },
  { key: "valide", label: "Valides" },
  { key: "a_recycler", label: "À revoir" },
  { key: "depasse", label: "Expirées" },
  { key: "non_forme", label: "Non formés" },
];

export function EpiBadge() {
  return (
    <span className="inline-flex items-center rounded-full border border-sky-700 bg-sky-950 px-2.5 py-0.5 text-xs font-bold text-sky-400" data-testid="epi-badge">
      Équipier de Première Intervention
    </span>
  );
}

export function IncendieBadge() {
  return (
    <span className="inline-flex items-center rounded-full border border-sky-700 bg-sky-950 px-2.5 py-0.5 text-xs font-bold text-sky-400" data-testid="incendie-badge">
      Formation incendie
    </span>
  );
}

function Jours({ jours }) {
  if (jours === null || jours === undefined) return <span className="text-xs text-muted-foreground">—</span>;
  const cls = jours < 0 ? "text-red-400" : jours <= 180 ? "text-amber-400" : "text-muted-foreground";
  const ans = Math.floor(Math.abs(jours) / 365);
  const mois = Math.round((Math.abs(jours) % 365) / 30.44);
  const txt = jours < 0
    ? `expirée depuis ${ans > 0 ? `${ans} an${ans > 1 ? "s" : ""} ` : ""}${mois > 0 ? `${mois} mois` : ""}`.trim()
    : `${ans > 0 ? `${ans} an${ans > 1 ? "s" : ""} ` : ""}${mois > 0 ? `${mois} mois` : `${Math.abs(jours)} j`}`.trim();
  return <span className={`text-xs font-mono ${cls}`}>{txt}</span>;
}

const EMPTY = { date_formation: "", date_validite: "" };

const IMPORT_STYLES = {
  ok: { label: "Appliqué", classes: "text-emerald-400", icon: CheckCircle2 },
  partiel: { label: "Rattaché (sans dates)", classes: "text-amber-400", icon: AlertTriangle },
  introuvable: { label: "Salarié introuvable", classes: "text-red-400", icon: XCircle },
  erreur: { label: "Erreur", classes: "text-red-400", icon: XCircle },
};

export default function Incendie() {
  const { user } = useAuth();
  const readOnly = !canDo(user, "incendie", "modifier");
  const canCreate = canDo(user, "incendie", "creer");
  const [searchParams] = useSearchParams();
  const [agents, setAgents] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [validiteMois, setValiditeMois] = useState(36);
  const [loading, setLoading] = useState(true);
  const [filtre, setFiltre] = useState(searchParams.get("statut") || "tous");
  const [manageAgent, setManageAgent] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ emp_id: "", ...EMPTY });
  const [editForm, setEditForm] = useState(EMPTY);
  const [editing, setEditing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFiles, setImportFiles] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const fileRef = useRef(null);
  const importRef = useRef(null);

  const load = useCallback(() => {
    api.get("/formations-incendie").then((r) => { setAgents(r.data.agents); setValiditeMois(r.data.validite_mois); })
      .catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get("/employees").then((r) => setAllEmployees(r.data.employees)).catch(() => {});
  }, [load]);

  const counts = agents.reduce((acc, a) => {
    const st = a.formation_incendie?.statut;
    if (st) acc[st] = (acc[st] || 0) + 1;
    return acc;
  }, {});
  const nonFormes = allEmployees.filter((e) => !e.formation_incendie);
  const couverts = (counts.valide || 0) + (counts.a_recycler || 0);
  const tauxCouverture = allEmployees.length ? Math.round((couverts / allEmployees.length) * 1000) / 10 : 0;
  const agentsFiltres = filtre === "tous" ? agents : agents.filter((a) => a.formation_incendie?.statut === filtre);

  const KPIS = [
    { key: "tous", label: "Personnes formées", value: agents.length, icon: Users, color: "text-sky-400", testid: "incendie-kpi-agents" },
    { key: "valide", label: "Formations valides", value: counts.valide || 0, icon: ShieldCheck, color: "text-emerald-400", testid: "incendie-kpi-valides" },
    { key: "a_recycler", label: "À revoir (< 6 mois)", value: counts.a_recycler || 0, icon: AlertTriangle, color: "text-amber-400", testid: "incendie-kpi-recycler" },
    { key: "depasse", label: "Expirées", value: counts.depasse || 0, icon: XOctagon, color: "text-red-400", testid: "incendie-kpi-expirees" },
    { key: "non_forme", label: "Non formés", value: nonFormes.length, icon: UserX, color: "text-gray-400", testid: "incendie-kpi-non-formes" },
    { key: "tous", label: "Taux de couverture", value: `${tauxCouverture} %`, icon: Gauge, color: "text-teal-400", testid: "incendie-kpi-couverture" },
  ];

  const openPreview = async (c) => {
    try {
      const r = await api.get(`/certificats/${c.id}/download`, { responseType: "blob" });
      setPreview({ url: URL.createObjectURL(r.data), filename: c.filename });
    } catch (e) {
      toast.error(formatApiError(e, "Aperçu impossible"));
    }
  };

  const download = (c) => downloadFile(`/certificats/${c.id}/download`, c.filename).catch(() => toast.error("Téléchargement impossible"));

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !manageAgent) return;
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        await api.post(`/employees/${manageAgent.id}/certificats?categorie=incendie`, form);
      }
      toast.success(files.length > 1 ? `${files.length} attestations téléversées — analyse en cours` : "Attestation téléversée — analyse en cours");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Échec du téléversement"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const runImport = async () => {
    if (!importFiles.length) return;
    setImporting(true);
    setImportResults(null);
    try {
      const form = new FormData();
      importFiles.forEach((f) => form.append("files", f));
      const { data } = await api.post("/formations-incendie/import", form, { timeout: 300000 });
      setImportResults(data.resultats);
      const ok = data.resultats.filter((r) => r.statut === "ok").length;
      toast.success(`${ok} formation(s) incendie appliquée(s) automatiquement`);
      load();
      api.get("/employees").then((r) => setAllEmployees(r.data.employees)).catch(() => {});
    } catch (err) {
      toast.error(formatApiError(err, "Échec de l'import"));
    } finally {
      setImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  const removeCert = async (c) => {
    if (!window.confirm(`Supprimer "${c.filename}" ?`)) return;
    try {
      await api.delete(`/certificats/${c.id}`);
      toast.success("Document supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const applyAnalyse = async (c) => {
    try {
      await api.post(`/certificats/${c.id}/appliquer`);
      toast.success("Formation incendie mise à jour depuis le document");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openManage = (a) => {
    setManageAgent(a);
    setEditing(false);
    setEditForm({ date_formation: a.formation_incendie?.date_formation || "", date_validite: a.formation_incendie?.date_validite || "" });
  };

  const saveFormation = async () => {
    setSaving(true);
    try {
      await api.put(`/employees/${manageAgent.id}/formation-incendie`, {
        date_formation: editForm.date_formation || null,
        date_validite: editForm.date_validite || null,
      });
      toast.success("Formation incendie mise à jour");
      setEditing(false);
      load();
      setManageAgent(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeFormation = async () => {
    if (!window.confirm(`Retirer la formation incendie de ${manageAgent.prenom} ${manageAgent.nom} ?`)) return;
    try {
      await api.delete(`/employees/${manageAgent.id}/formation-incendie`);
      toast.success("Formation incendie retirée");
      setManageAgent(null);
      load();
      api.get("/employees").then((r) => setAllEmployees(r.data.employees)).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addAgent = async () => {
    if (!addForm.emp_id) {
      toast.error("Choisissez un salarié");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/employees/${addForm.emp_id}/formation-incendie`, {
        date_formation: addForm.date_formation || null,
        date_validite: addForm.date_validite || null,
      });
      toast.success("Formation incendie ajoutée");
      setAddOpen(false);
      setAddForm({ emp_id: "", ...EMPTY });
      load();
      api.get("/employees").then((r) => setAllEmployees(r.data.employees)).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const currentAgent = manageAgent ? agents.find((a) => a.id === manageAgent.id) || manageAgent : null;

  return (
    <div className="space-y-6" data-testid="incendie-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-sky-500 mb-1">Équipiers de première intervention</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
            <FireExtinguisher className="h-8 w-8 text-sky-500" /> Formations <span className="text-sky-500">Incendie</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Validité {validiteMois} mois (réglable dans Rappels) · rappels automatiques à 6, 3 et 1 mois avant expiration</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ExportDialog type="incendie" filename="export_formations_incendie.xlsx" testid="export-incendie-button" />
          {canCreate && (
            <Button variant="outline" onClick={() => { setImportOpen(true); setImportFiles([]); setImportResults(null); }} className="border-sky-800 text-sky-400" data-testid="import-incendie-button">
              <FolderUp className="h-4 w-4 mr-2" /> Import PDF (auto)
            </Button>
          )}
          {canCreate && (
            <Button onClick={() => setAddOpen(true)} className="bg-sky-600 hover:bg-sky-700" data-testid="add-incendie-button">
              <Plus className="h-4 w-4 mr-2" /> Ajouter une formation incendie
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {KPIS.map((k) => (
          <div key={k.testid} onClick={() => setFiltre(k.key)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setFiltre(k.key)}
            className={`rounded-xl border p-4 cursor-pointer transition-all hover:-translate-y-0.5 hover:ring-1 hover:ring-sky-500/60 ${filtre === k.key && k.key !== "tous" ? "border-sky-500 bg-sky-950/40 ring-1 ring-sky-500/60" : "border-sky-900/50 bg-sky-950/20"}`}
            data-testid={k.testid}>
            <div className="flex items-center justify-between mb-2">
              <k.icon className={`h-4 w-4 ${k.color}`} />
            </div>
            <p className={`text-2xl font-extrabold font-mono ${k.color}`} data-testid={`${k.testid}-value`}>{k.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{k.label}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="incendie-filters">
        {FILTRES.map((f) => (
          <button key={f.key} onClick={() => setFiltre(f.key)} data-testid={`incendie-filter-${f.key}`}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${filtre === f.key ? "border-sky-500 bg-sky-950/60 text-sky-400" : "border-border text-muted-foreground hover:text-foreground"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {filtre === "non_forme" ? (
        <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="incendie-non-formes">
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm font-semibold">Salariés sans formation incendie ({nonFormes.length})</p>
            <p className="text-xs text-muted-foreground">Ces salariés n'ont aucune formation incendie enregistrée.</p>
          </div>
          <div className="divide-y divide-border/50">
            {nonFormes.length === 0 && <p className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="incendie-non-formes-empty">Tous les salariés sont formés.</p>}
            {nonFormes.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`incendie-nonforme-${e.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{e.nom} {e.prenom}</p>
                  <p className="text-xs text-muted-foreground">{e.service}</p>
                </div>
                {canCreate && (
                  <Button size="sm" variant="outline" className="border-sky-800 text-sky-400 shrink-0"
                    onClick={() => { setAddForm({ emp_id: e.id, ...EMPTY }); setAddOpen(true); }} data-testid={`incendie-nonforme-add-${e.id}`}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Former
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-border bg-card overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="incendie-table">
                <thead>
                  <tr className="border-b border-border text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3">Salarié</th>
                    <th className="px-4 py-3">Service</th>
                    <th className="px-4 py-3"><span className="text-sky-400">Date de formation</span></th>
                    <th className="px-4 py-3">Fin de validité</th>
                    <th className="px-4 py-3">Délai</th>
                    <th className="px-4 py-3">Documents</th>
                    <th className="px-4 py-3">Statut</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Chargement...</td></tr>}
                  {!loading && agentsFiltres.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground" data-testid="incendie-empty">{filtre === "tous" ? "Aucune formation incendie enregistrée — ajoutez vos salariés formés." : "Aucune formation avec ce statut."}</td></tr>}
                  {!loading && agentsFiltres.map((a) => (
                    <tr key={a.id} className="border-b border-border/50 hover:bg-secondary/40 transition-colors" data-testid={`incendie-row-${a.id}`}>
                      <td className="px-4 py-3"><span className="font-medium">{a.nom}</span> {a.prenom}</td>
                      <td className="px-4 py-3 text-muted-foreground">{a.service}</td>
                      <td className="px-4 py-3 font-mono text-xs">{a.formation_incendie?.date_formation || "—"}</td>
                      <td className="px-4 py-3 font-mono text-xs">{a.formation_incendie?.date_validite || "—"}</td>
                      <td className="px-4 py-3"><Jours jours={a.formation_incendie?.jours_restants} /></td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground" data-testid={`incendie-docs-${a.id}`}>{a.certificats?.length || 0} PDF</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUTS[a.formation_incendie?.statut || "inconnu"].classes}`}>
                          {STATUTS[a.formation_incendie?.statut || "inconnu"].label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" title="Gérer" onClick={() => openManage(a)} data-testid={`incendie-manage-${a.id}`}>
                          <FileText className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="md:hidden space-y-2" data-testid="incendie-cards">
            {loading && <p className="py-8 text-center text-muted-foreground">Chargement...</p>}
            {!loading && agentsFiltres.length === 0 && <p className="py-8 text-center text-muted-foreground">{filtre === "tous" ? "Aucune formation incendie enregistrée — ajoutez vos salariés formés." : "Aucune formation avec ce statut."}</p>}
            {!loading && agentsFiltres.map((a) => (
              <div key={a.id} className="rounded-xl border border-border bg-card p-4 space-y-2.5" data-testid={`incendie-card-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold">{a.nom} {a.prenom}</p>
                    <p className="text-xs text-muted-foreground">{a.service} · {a.certificats?.length || 0} PDF</p>
                  </div>
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUTS[a.formation_incendie?.statut || "inconnu"].classes}`}>
                    {STATUTS[a.formation_incendie?.statut || "inconnu"].label}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-mono text-muted-foreground">
                  <span>Formation : {a.formation_incendie?.date_formation || "—"}</span>
                  <span>Validité : {a.formation_incendie?.date_validite || "—"}</span>
                  <Jours jours={a.formation_incendie?.jours_restants} />
                </div>
                <Button variant="outline" size="sm" className="w-full h-10" onClick={() => openManage(a)} data-testid={`incendie-manage-m-${a.id}`}>
                  <FileText className="h-4 w-4 mr-1.5" /> Gérer la formation
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!manageAgent} onOpenChange={(o) => !o && setManageAgent(null)}>
        <DialogContent className="max-w-2xl" data-testid="incendie-manage-dialog">
          {currentAgent && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FireExtinguisher className="h-5 w-5 text-sky-500" /> {currentAgent.prenom} {currentAgent.nom}
                </DialogTitle>
                <DialogDescription>{currentAgent.service} — formation incendie et attestations PDF</DialogDescription>
              </DialogHeader>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">Formation incendie</Label>
                  {!readOnly && !editing && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(true)} data-testid="incendie-edit-button"><Pencil className="h-3.5 w-3.5 mr-1" /> Modifier</Button>
                      <Button size="sm" variant="ghost" className="hover:text-red-400" onClick={removeFormation} data-testid="incendie-remove-button"><Trash2 className="h-3.5 w-3.5 mr-1" /> Retirer</Button>
                    </div>
                  )}
                </div>
                {!editing ? (
                  <div className="flex items-center gap-3 flex-wrap rounded-lg border border-border px-3 py-2.5" data-testid="incendie-card-view">
                    <IncendieBadge />
                    <span className="text-xs font-mono">formation : {currentAgent.formation_incendie?.date_formation || "—"}</span>
                    <span className="text-xs font-mono">validité : {currentAgent.formation_incendie?.date_validite || "—"}</span>
                    <Jours jours={currentAgent.formation_incendie?.jours_restants} />
                  </div>
                ) : (
                  <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-border p-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Date de formation</Label>
                      <Input type="date" value={editForm.date_formation} onChange={(e) => setEditForm({ ...editForm, date_formation: e.target.value })} className="bg-background w-40" data-testid="incendie-form-formation" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Fin de validité <span className="text-muted-foreground">(auto +{validiteMois} mois)</span></Label>
                      <Input type="date" value={editForm.date_validite} onChange={(e) => setEditForm({ ...editForm, date_validite: e.target.value })} className="bg-background w-40" data-testid="incendie-form-validite" />
                    </div>
                    <Button size="sm" onClick={saveFormation} disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="incendie-form-submit">Enregistrer</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Annuler</Button>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Attestations PDF ({currentAgent.certificats?.length || 0})</Label>
                {!readOnly && (
                  <div>
                    <input ref={fileRef} type="file" accept=".pdf,application/pdf" multiple className="hidden" onChange={upload} data-testid="incendie-cert-input" />
                    <Button size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="incendie-cert-upload">
                      {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                      Téléverser des attestations (PDF, plusieurs possibles)
                    </Button>
                  </div>
                )}
                {currentAgent.certificats?.length === 0 && <p className="text-xs text-muted-foreground">Aucun document téléversé.</p>}
                {currentAgent.certificats?.map((c) => (
                  <div key={c.id} className="rounded-lg border border-border" data-testid={`incendie-cert-${c.id}`}>
                    <div className="flex items-center justify-between px-3 py-2">
                      <button onClick={() => openPreview(c)} className="flex items-center gap-2 text-sm min-w-0 text-left hover:text-sky-400 transition-colors">
                        <FileText className="h-4 w-4 text-sky-400 shrink-0" />
                        <span className="truncate hover:underline underline-offset-2">{c.filename}</span>
                        <span className="text-xs text-muted-foreground font-mono shrink-0">{c.created_at?.slice(0, 10)}</span>
                      </button>
                      <div className="flex items-center shrink-0">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openPreview(c)} data-testid={`incendie-cert-view-${c.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => download(c)} data-testid={`incendie-cert-dl-${c.id}`}><Download className="h-3.5 w-3.5" /></Button>
                        {!readOnly && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-400" onClick={() => removeCert(c)} data-testid={`incendie-cert-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        )}
                      </div>
                    </div>
                    {c.analyse?.statut === "en_cours" && (
                      <p className="flex items-center gap-2 text-xs text-muted-foreground px-3 pb-2"><Loader2 className="h-3.5 w-3.5 animate-spin text-sky-400" /> Analyse automatique en cours...</p>
                    )}
                    {c.analyse?.statut === "termine" && (
                      <div className="mx-3 mb-2 rounded-lg border border-sky-900/50 bg-sky-950/20 p-3 space-y-1.5" data-testid={`incendie-analysis-${c.id}`}>
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-sky-400"><Sparkles className="h-3.5 w-3.5" /> Analyse automatique (confiance : {c.analyse.confiance})</p>
                        <p className="text-xs">Détecté : <span className="font-medium">{c.analyse.prenom || "?"} {c.analyse.nom || "?"}</span>
                          {c.analyse.date_formation && <> · formation <span className="font-mono">{c.analyse.date_formation}</span></>}
                          {c.analyse.date_validite && <> · validité <span className="font-mono">{c.analyse.date_validite}</span></>}
                        </p>
                        {!c.analyse.correspondance && (
                          <p className="flex items-center gap-1.5 text-xs text-red-400"><AlertTriangle className="h-3.5 w-3.5" /> Le nom détecté ne correspond pas à ce salarié.</p>
                        )}
                        {c.analyse.appliquee ? (
                          <p className="flex items-center gap-1.5 text-xs text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> Dates appliquées à la formation</p>
                        ) : !readOnly && (c.analyse.date_validite || c.analyse.date_formation) && (
                          <Button size="sm" variant="outline" onClick={() => applyAnalyse(c)} data-testid={`incendie-apply-${c.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Appliquer les dates
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportFiles([]); setImportResults(null); } }}>
        <DialogContent className="max-w-xl" data-testid="incendie-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FolderUp className="h-5 w-5 text-sky-500" /> Import PDF automatique</DialogTitle>
            <DialogDescription>Déposez jusqu'à 10 attestations PDF : chaque document est analysé, le salarié est identifié par son nom et sa formation incendie est créée ou mise à jour automatiquement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <input ref={importRef} type="file" accept=".pdf,application/pdf" multiple className="hidden"
                onChange={(e) => setImportFiles(Array.from(e.target.files || []).slice(0, 10))} data-testid="incendie-import-input" />
              <Button variant="outline" className="w-full border-dashed h-16" onClick={() => importRef.current?.click()} disabled={importing} data-testid="incendie-import-pick">
                <Upload className="h-4 w-4 mr-2" /> {importFiles.length ? `${importFiles.length} fichier(s) sélectionné(s)` : "Choisir des fichiers PDF"}
              </Button>
            </div>
            {importFiles.length > 0 && !importResults && (
              <ul className="text-xs text-muted-foreground space-y-1 max-h-32 overflow-y-auto" data-testid="incendie-import-list">
                {importFiles.map((f, i) => <li key={i} className="flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-sky-400" /> {f.name}</li>)}
              </ul>
            )}
            {importing && (
              <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="incendie-import-progress">
                <Loader2 className="h-4 w-4 animate-spin text-sky-400" /> Analyse des documents en cours (quelques secondes par fichier)...
              </p>
            )}
            {importResults && (
              <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="incendie-import-results">
                {importResults.map((r, i) => {
                  const st = IMPORT_STYLES[r.statut] || IMPORT_STYLES.erreur;
                  return (
                    <div key={i} className="flex items-start gap-2.5 rounded-lg border border-border px-3 py-2" data-testid={`incendie-import-result-${i}`}>
                      <st.icon className={`h-4 w-4 mt-0.5 shrink-0 ${st.classes}`} />
                      <div className="min-w-0 text-xs">
                        <p className="font-medium text-sm truncate">{r.filename}{r.employe ? <span className="text-muted-foreground"> → {r.employe}</span> : null}</p>
                        <p className={st.classes}>{st.label} — {r.detail}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Fermer</Button>
            {!importResults && (
              <Button onClick={runImport} disabled={importing || !importFiles.length} className="bg-sky-600 hover:bg-sky-700" data-testid="incendie-import-submit">
                {importing ? "Analyse en cours..." : "Lancer l'import"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="incendie-add-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FireExtinguisher className="h-5 w-5 text-sky-500" /> Ajouter une formation incendie</DialogTitle>
            <DialogDescription>Associez une formation incendie à un salarié existant.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Salarié *</Label>
              <EmployeePicker employees={allEmployees.filter((e) => !e.formation_incendie)} value={addForm.emp_id}
                onChange={(v) => setAddForm({ ...addForm, emp_id: v })} testid="incendie-add-employee" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date de formation</Label>
                <Input type="date" value={addForm.date_formation} onChange={(e) => setAddForm({ ...addForm, date_formation: e.target.value })} className="bg-background" data-testid="incendie-add-formation" />
              </div>
              <div className="space-y-2">
                <Label>Fin de validité <span className="text-muted-foreground text-xs">(auto)</span></Label>
                <Input type="date" value={addForm.date_validite} onChange={(e) => setAddForm({ ...addForm, date_validite: e.target.value })} className="bg-background" data-testid="incendie-add-validite" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Annuler</Button>
            <Button onClick={addAgent} disabled={saving} className="bg-sky-600 hover:bg-sky-700" data-testid="incendie-add-submit">
              {saving ? "Enregistrement..." : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); } }}>
        <DialogContent className="max-w-4xl" data-testid="incendie-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-sky-400" /> {preview?.filename}</DialogTitle>
            <DialogDescription>Aperçu du document PDF</DialogDescription>
          </DialogHeader>
          {preview && <iframe src={preview.url} title={preview.filename} className="w-full h-[70vh] rounded-lg border border-border bg-white" data-testid="incendie-preview-frame" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
